import { Plus } from '@phosphor-icons/react';
import {
  getListCaptionStylesQueryKey,
  useCreateCaptionStyle,
  useDeleteCaptionStyle,
} from '@reelplicate/api-client/caption-styles';
import {
  getListWorkspacesQueryKey,
  useListWorkspaces,
  useUpdateWorkspace,
} from '@reelplicate/api-client/workspaces';
import { DEFAULT_CAPTION_STYLE } from '@reelplicate/flows';
import {
  Button,
  Inline,
  Select,
  SelectDivider,
  SelectGroup,
  SelectHeader,
  SelectItem,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import {
  SOURCE_LABELS,
  type StyleOption,
  useCaptionStyleOptions,
} from '../caption-styles/caption-style-options';
import { StyleCard } from '../caption-styles/style-card';
import { type EditorStart, StyleEditor } from '../caption-styles/style-editor';
import { SettingsCard, SettingsSection } from './settings-layout';

/**
 * Brand profile → Caption styles (Figma 400:33986): the style new carousels
 * use, the workspace's own styles, and the presets to start from.
 */
export function CaptionStylesSection({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.find((w) => w.id === workspaceId);
  const styles = useCaptionStyleOptions(workspaceId);
  const updateWorkspace = useUpdateWorkspace();
  const create = useCreateCaptionStyle();
  const remove = useDeleteCaptionStyle();
  const [editor, setEditor] = useState<EditorStart | null>(null);
  const listKey = getListCaptionStylesQueryKey(workspaceId);

  const setDefault = (ref: string) =>
    updateWorkspace.mutate(
      {
        id: workspaceId,
        data: { defaultCaptionStyle: ref === DEFAULT_CAPTION_STYLE ? '' : ref },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getListWorkspacesQueryKey() });
          toast.success('Default caption style saved');
        },
      },
    );
  const duplicate = (option: StyleOption) =>
    create.mutate(
      { id: workspaceId, data: { name: `${option.name} copy`, style: option.style } },
      { onSuccess: () => void queryClient.invalidateQueries({ queryKey: listKey }) },
    );
  const del = (option: StyleOption) =>
    remove.mutate(
      { id: workspaceId, styleId: option.ref },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: listKey });
          toast.success(
            option.usedBy
              ? `Deleted “${option.name}”. Flows using it switch to the default.`
              : `Deleted “${option.name}”`,
          );
        },
      },
    );

  const presetGrid = (source: 'tiktok' | 'instagram') => (
    <div className="flex flex-wrap gap-3">
      {styles.presets
        .filter((p) => p.source === source)
        .map((option) => (
          <StyleCard
            key={option.ref}
            option={option}
            onSelect={() =>
              setEditor({
                mode: 'create',
                name: `${option.name} (mine)`,
                style: option.style,
              })
            }
          />
        ))}
    </div>
  );

  return (
    <SettingsSection
      title="Caption styles"
      description="How text looks on carousel slides. Every flow can use these; a slide can pick its own."
    >
      <SettingsCard className="py-5 [&>*]:border-0">
        <Stack gap={6}>
          <Inline gap={4} alignY="center">
            <Stack gap={0.5} className="min-w-0 flex-1">
              <span className="text-label-md text-text-primary">
                Default for new carousels
              </span>
              <span className="text-caption text-text-secondary">
                Carousels without a style of their own use this.
              </span>
            </Stack>
            <Select
              aria-label="Default caption style"
              className="w-65"
              value={workspace?.defaultCaptionStyle ?? DEFAULT_CAPTION_STYLE}
              onValueChange={setDefault}
            >
              {(['tiktok', 'instagram'] as const).map((source, i) => (
                <Fragment key={source}>
                  {i > 0 && <SelectDivider />}
                  <SelectGroup>
                    <SelectHeader>{SOURCE_LABELS[source]}</SelectHeader>
                    {styles.presets
                      .filter((p) => p.source === source)
                      .map((p) => (
                        <SelectItem key={p.ref} value={p.ref}>
                          {`${SOURCE_LABELS[source]} · ${p.name}`}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </Fragment>
              ))}
              {styles.custom.length > 0 && (
                <>
                  <SelectDivider />
                  <SelectGroup>
                    <SelectHeader>Your styles</SelectHeader>
                    {styles.custom.map((c) => (
                      <SelectItem key={c.ref} value={c.ref}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
            </Select>
          </Inline>

          <Stack gap={3}>
            <Inline gap={3} alignY="center">
              <Stack gap={0.5} className="min-w-0 flex-1">
                <span className="text-body-sm-strong text-text-primary">Your styles</span>
                <span className="text-caption text-text-secondary">
                  Changing one updates every flow that uses it.
                </span>
              </Stack>
              <Button
                type="button"
                size="sm"
                variant="outline"
                leadingIcon={Plus}
                onClick={() =>
                  setEditor({ mode: 'create', style: styles.presets[0]!.style })
                }
              >
                New style
              </Button>
            </Inline>
            {styles.loaded && styles.custom.length === 0 ? (
              <p className="text-body-sm text-text-secondary">
                None yet. Start from a preset below, or make one from scratch.
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {styles.custom.map((option) => (
                  <StyleCard
                    key={option.ref}
                    option={option}
                    onEdit={() =>
                      setEditor({
                        mode: 'edit',
                        id: option.ref,
                        name: option.name,
                        style: option.style,
                      })
                    }
                    onDuplicate={() => duplicate(option)}
                    onDelete={() => del(option)}
                  />
                ))}
              </div>
            )}
          </Stack>

          <Stack gap={3}>
            <Stack gap={0.5}>
              <span className="text-body-sm-strong text-text-primary">Presets</span>
              <span className="text-caption text-text-secondary">
                Looks inspired by TikTok and Instagram text. Pick one to make it yours.
              </span>
            </Stack>
            <Tabs defaultValue="tiktok">
              <TabList variant="pill" aria-label="Preset source">
                <Tab value="tiktok">TikTok</Tab>
                <Tab value="instagram">Instagram</Tab>
              </TabList>
              <TabPanel value="tiktok" className="pt-3">
                {presetGrid('tiktok')}
              </TabPanel>
              <TabPanel value="instagram" className="pt-3">
                {presetGrid('instagram')}
              </TabPanel>
            </Tabs>
          </Stack>
        </Stack>
      </SettingsCard>
      <StyleEditor
        workspaceId={workspaceId}
        start={editor}
        onOpenChange={(open) => !open && setEditor(null)}
      />
    </SettingsSection>
  );
}
